import { Property, Type, TypeManager } from '@dipscope/type-manager';
import TileSegment from '../tilebase/TileSegment';
import { NodeDef, TileCountMap, TileDef, TileGameConfig } from '../tilebase/TileGameConfig';
import PlayerList from '../../core/Playerlist';
import { tileMapConfig } from './kGameConfigLoader';
import _ from 'lodash';
import KPlayer from './KPlayer';
import 'reflect-metadata';
import KTile, {
  hexaPlaceType,
  hexaSlotToSideIndex,
  KActionDTO,
  KPosition,
  KResourceActionDTO,
  KResourceMap,
  KSubmitActionDTO,
  KTaxableNodeMap,
  KTaxActionDTO,
  KTileDTO,
  Place,
  ResourceMove,
  rhombusSlotToSideIndex,
  TaxPlacement,
  toPlace,
  triangleSlotToSideIndex
} from './KTile';
import KNode, { KEntitType, KEntitTypeMap } from './KNode';
import { HexaTopology, KTileNbs } from './HexaTolopogy';
import { Meeple, MeepleType } from '../tilebase/Meeple';
import logger from '../../logger';
import { KTileDef } from './KTileGameConfig';
import { KTileData } from './KTileData';
import KTileSegment from './KTileSegment';
import { KPlayerResource, KResource } from './KResource';
import KMeeple from './KMeeple';
import { TurnType } from './KEnv';

@Type({ injectable: true })
class KNodeSet extends Set<KNode> {}

@Type({ discriminant: 'KTileLogic', injectable: true })
class KTileLogic {
  @Property()
  hexaSegment: KTileSegment;

  @Property()
  rhombusSegment: KTileSegment;

  @Property()
  triangleSegment: KTileSegment;

  @Property([KTile])
  lastTiles: KTile[] = [];

  @Property()
  tileData: KTileData;

  @Property([KPlayer])
  playerList: PlayerList<KPlayer>;

  @Property()
  topology: HexaTopology;

  @Property([KNodeSet])
  compEntityArr: Array<KNodeSet> = [];

  @Property()
  entityMap: KEntitTypeMap;

  tileClass: { new (tileDef: KTileDef): KTile };

  @Property()
  gameConfig: TileGameConfig;

  @Property()
  tileCountConfig: TileCountMap;

  @Property()
  tileMapConfig: Record<number, KTileDef>;

  @Property()
  idCount: number = 0;

  @Property([KActionDTO])
  currentActions: Array<KActionDTO> = [];

  @Property()
  lastAction: KTileDTO;

  @Property()
  turn: TurnType = 'tile';

  @Property([Meeple])
  recoveredMeeples: Meeple[] = [];

  nodeType(nodeDef: NodeDef) {
    return new KNode(nodeDef);
  }

  init(gameConfig: TileGameConfig, tileCountConfig: TileCountMap, playerList: PlayerList<KPlayer>) {
    this.gameConfig = _.cloneDeep(gameConfig);
    this.tileCountConfig = _.cloneDeep(tileCountConfig);
    this.tileMapConfig = tileMapConfig;

    this.entityMap = new KEntitTypeMap();
    this.topology = new HexaTopology();
    this.tileData = new KTileData(this.topology);

    this.playerList = playerList;

    this.tileClass = KTile;

    this.createTileSegments();

    this.placeStartTile();
  }

  placeStartTile() {
    // pull startTile
    const startTileType = this.gameConfig.startTile;
    const startTile = this.hexaSegment.tiles.find((t) => t.type == startTileType);
    if (!startTile) return;
    this.hexaSegment.tiles.splice(this.hexaSegment.tiles.indexOf(startTile), 1);
    // place startTile (hexa)
    const startPlace = new Place('hexa', [
      new KPosition().init(0, 0, 0),
      new KPosition().init(0, 0, 1),
      new KPosition().init(0, 0, 2),
      new KPosition().init(0, 0, 3),
      new KPosition().init(0, 0, 4),
      new KPosition().init(0, 0, 5)
    ]);
    startTile.place = startPlace;
    this.tileData.set(startPlace, startTile);
    return startTile;
  }

  createTileSegments() {
    this.idCount = 0;
    const tileGroups = this.gameConfig.segments.split('|');

    const hexaTiles = this.createTileList(tileGroups[0]);
    this.hexaSegment = {
      isRandom: this.gameConfig.random,
      tiles: hexaTiles
    };
    this.idCount++;
    const rhombusTiles = this.createTileList(tileGroups[1]);
    this.rhombusSegment = {
      isRandom: this.gameConfig.random,
      tiles: rhombusTiles
    };

    this.idCount++;
    const triangleTiles = this.createTileList(tileGroups[2]);
    this.triangleSegment = {
      isRandom: this.gameConfig.random,
      tiles: triangleTiles
    };
  }

  castleIds: [50, 51, 52, 53, 54, 55];

  createAssetTiles() {
    const assetTiles: KTile[] = [];
    //  k mini - there are 8 hexa tiles after placing the first tile - 2 hexa per player
    //  koenigsbourg - there are 12 hexa tiles after placing the first tile - 3 hexa per player
    const hexaCount = this.gameConfig.type === 'koenigsburg_mini' ? 2 : 3;
    for (let i = 0; i < hexaCount; i++) {
      if (this.hexaSegment.tiles.length == 0) break;
      let index = Math.floor(Math.random() * this.hexaSegment.tiles.length);
      assetTiles.push(this.hexaSegment.tiles.splice(index, 1)[0]);
    }

    return assetTiles;
  }

  createTileList(tg: string): KTile[] {
    const tileList = [];
    const tileCountMap = this.tileCountConfig.get(tg);
    if (!tileCountMap) return tileList;
    Array.from(tileCountMap.keys()).forEach((tileType: number) => {
      const count = tileCountMap.get(tileType);
      for (let i = 0; i < count; i++) {
        const tile = this.createTile(tileType, this.idCount);
        this.idCount++;
        tileList.push(tile);
      }
    });

    return tileList;
  }

  createTile(tileType: number, tileId: number): KTile {
    const tileDef = tileMapConfig[tileType];
    const clonedTileDef = _.cloneDeep(tileDef);
    const tile = new KTile(clonedTileDef);
    tile.id = tileId;
    tile.nodes = clonedTileDef.nodes.map((nodeDef) => {
      const cloneNodeDef = _.cloneDeep(nodeDef);
      const node = this.nodeType(cloneNodeDef);
      node.tile = tile;
      return node;
    });
    tile.place = new Place(clonedTileDef.shape, []);
    tile.sides = clonedTileDef.sides;
    return tile;
  }

  // pulls 2 triangle, 1 rhombus tile into lastTiles
  pullTiles() {
    this.lastTiles = [];
    // no more resource tiles
    if (this.rhombusSegment.tiles.length == 0 || this.triangleSegment.tiles.length < 2) {
      return null;
    }
    // pull 2 triangles
    this.pull(this.triangleSegment);
    this.pull(this.triangleSegment);
    // pull 1 rhombus
    this.pull(this.rhombusSegment);

    logger.info('lastTiles', this.lastTiles);
  }

  // pull random or fixed tile
  pull(segment: KTileSegment) {
    let index = Math.floor(Math.random() * segment.tiles.length);
    if (!this.gameConfig.random) index = 0;
    this.lastTiles.push(segment.tiles.splice(index, 1)[0]);
  }

  tilesLeft() {
    return this.rhombusSegment.tiles.length;
  }

  pushbackTiles() {
    while (this.lastTiles.length > 0) {
      const tile = this.lastTiles.pop();
      if (tile == null) continue;
      if (tile.shape === 'hexa') {
        this.hexaSegment.tiles.push(tile);
      } else if (tile.shape === 'rhombus') {
        this.rhombusSegment.tiles.push(tile);
      } else if (tile.shape === 'triangle') {
        this.triangleSegment.tiles.push(tile);
      }
    }
  }

  skipTile() {
    if (this.tilesLeft() == 0) {
      this.lastTiles = [];
    } else {
      this.pushbackTiles();
    }
    this.turn = 'tile';
  }

  applyAction(submitAction: KSubmitActionDTO) {
    // log exactly what is being submitted vs. the current pull, so a stale/mismatched submit
    // (submitted tile not in lastTiles -> "Invalid tile placed") is obvious in the server log
    logger.info(
      'applyAction submit tiles=[%s] lastTiles=[%s] assets=[%s]',
      submitAction.tiles.map((t) => `${t.id}:${t.shape}`).join(','),
      this.lastTiles.map((t) => `${t.id}:${t.shape}`).join(','),
      this.playerList.actPlayer.assets.map((t) => `${t.id}:${t.shape}`).join(',')
    );
    submitAction.tiles.forEach((tile: KTileDTO) => {
      // add hexa asset to lastTiles
      if (tile.shape === 'hexa') {
        this.addAssetTile(tile);
      }
      this.placeTile({ ...tile, isTemp: false });
    });
    this.lastTiles.forEach((tile) => {
      this.setNodeConnections(tile, this.tileData);
    });
    this.evaluateTileUpdate();
  }

  applyResourceAction(resourceAction: KResourceActionDTO) {
    this.taxableNodeMap = new KTaxableNodeMap();
    resourceAction.resources.forEach((resourceMove) => {
      const { resource, targetNodeId, targetTileId } = resourceMove;
      // find source node
      const sourceTile = this.tileById(resource.tileId);
      const sourceNode = sourceTile.nodes.find((n) => n.id === resource.nodeId);
      if (!sourceNode) {
        logger.error('Source node not found for resource move.', resourceMove);
        return;
      }
      // find target node
      const targetTile = this.tileById(targetTileId);
      const portNode = targetTile.nodes.find((n) => n.id === targetNodeId);
      // targetNodeId is a port node, find connected city node
      const cityNodeId = portNode.cities[0];
      const cityNode = targetTile.nodes.find((n) => n.id === cityNodeId);
      if (!cityNode) {
        logger.error('Target node not found for resource move.', resourceMove);
        return;
      }
      // create or update player resource map for targetNode
      if (!targetTile.playerResource) {
        targetTile.playerResource = new KPlayerResource();
      }
      if (!targetTile.playerResource.map.has(resource.playerId)) {
        const copyResource = new KResource();
        copyResource.id = -resource.id;
        copyResource.playerId = resource.playerId;
        copyResource.type = resource.type;
        copyResource.value = resource.value;
        copyResource.color = resource.color;
        copyResource.nodeId = targetNodeId;
        copyResource.tileId = targetTileId;
        targetTile.playerResource.map.set(resource.playerId, copyResource);
      }
      // aggregate score for player
      targetTile.playerResource.map.get(resource.playerId).score += resource.value;
      // harvest old resource
      sourceNode.resource.harvested = true;
      // check if tax has to be payed to another player
      if (portNode.meeple && portNode.meeple.playerId !== resource.playerId) {
        targetTile.playerResource.map.get(portNode.meeple.playerId).score += resource.value / 2;
      }
      // check for recoverable taxmans ?
      this.checkCompletableRoad(portNode);
      // store free port nodes as taxable nodes - except if they are already in a completed road graph
      const cityTileId = cityNode.tile.id;
      if (!portNode.meeple && this.compEntityByNode(portNode) == null) {
        if (!this.taxableNodeMap.map.has(cityTileId)) {
          this.taxableNodeMap.map.set(cityTileId, new TaxPlacement(cityTileId));
        }
        if (!this.taxableNodeMap.map.get(cityTileId).portNodes.includes(portNode)) {
          this.taxableNodeMap.map.get(cityTileId).portNodes.push(portNode);
        }
      }
      // add mines
      if (sourceNode.mine) targetTile.mineCount++;
    });
    this.harvestableResources = new KResourceMap();
    if (Array.from(this.taxableNodeMap.map.keys()).length > 0) {
      this.turn = 'taxman';
    } else {
      this.taxableNodeMap = new KTaxableNodeMap();
      this.harvestableResources = new KResourceMap();
      this.turn = 'tile';
    }
  }

  applyTaxAction(taxAction: KTaxActionDTO) {
    taxAction.taxNodes.forEach((taxPlacement) => {
      const { tileId, nodeId, meepleId, playerId } = taxPlacement;
      const tile = this.tileById(tileId);
      const node: KNode = this.nodeById(tile, nodeId);
      // get player meeple by type
      const meeple = this.playerList.actPlayer.activateMeepleByType(MeepleType.meeple);
      if (meeple == null) {
        throw new Error('Tax Meeple cannot be placed.');
      }
      this.addTaxMeeple(meeple, node, tile);
      // check for recoverable taxmans - it might be immediately recoverable if placed on a completed road
      this.checkCompletableRoad(node);
    });
    this.taxableNodeMap = new KTaxableNodeMap();
    this.turn = 'tile';
  }

  applyTempAction(kActionDTO: KActionDTO) {
    if (kActionDTO.tile) {
      const tempTile: KTileDTO = { ...kActionDTO.tile, isTemp: true };
      this.placeTempTile(tempTile);
    }
  }

  updateTempAction(kActionDTO: KActionDTO) {
    if (!kActionDTO.tile) return;
    const tempTile: KTileDTO = { ...kActionDTO.tile, isTemp: true };
    this.updateTempTile(tempTile);
  }

  addAssetTile(tile: KTileDTO) {
    const player = this.playerList.actPlayer;
    const index = player.assets.findIndex((t) => t.id == tile.id);
    const hexaTile = player.assets.splice(index, 1)[0];
    logger.info('addAssetTile', hexaTile);
    this.lastTiles.push(hexaTile);
  }

  placeTile(tileDTO: KTileDTO) {
    logger.info('Place tile.', tileDTO);

    const lastTile = this.lastTiles.find((t) => t.id == tileDTO.id);
    if (lastTile == null) {
      logger.error('Invalid tile placed.', tileDTO, this.lastTiles);
      return;
    }

    this.lastAction = tileDTO;

    this.recoveredMeeples = [];

    this.updateTile(tileDTO, lastTile);

    this.tileData.set(lastTile.place, lastTile);
  }

  placeTempTile(tileDTO: KTileDTO) {
    logger.info('Place temp tile.', tileDTO);

    const lastTile = this.lastTiles.find((t) => t.id == tileDTO.id);
    if (lastTile == null) {
      logger.error('Invalid tile placed.', tileDTO, this.lastTiles);
      return;
    }

    this.lastAction = tileDTO;

    this.updateTile(tileDTO, lastTile);

    // key off the rebuilt place (real KPosition instances), NOT tileDTO.place: a bot action arrives
    // as plain JSON so tileDTO.place.positions have no KPosition prototype and their toString()
    // returns "[object Object]", collapsing every cell to one map key ("place already exists").
    this.tileData.set(lastTile.place, lastTile);
  }

  removeTempTiles() {
    logger.info('Remove temp tiles.');

    this.lastTiles.forEach((tile) => {
      if (tile.isTemp) {
        this.tileData.removeTile(tile.toDTO());
      }
    });
    this.lastTiles = [];
  }

  updateTempTile(tileDTO: KTileDTO) {
    const existingTile = this.tileData.getById(tileDTO.id);
    if (!existingTile) return;
    // remove old tile
    this.tileData.removeTile(existingTile.toDTO());
    this.updateTile(tileDTO, existingTile);
    // set new tile - key off the rebuilt place (real KPositions), not the plain-JSON tileDTO.place
    this.tileData.set(existingTile.place, existingTile);

    //this.tileData.updateOpenPlaces();
  }

  tiles(): KTileDTO[] {
    return Array.from(this.tileData.values()).map((t) => t.toDTO());
  }

  checkCompletableWald(startNode: KNode) {
    const updatedMeeples: Set<Meeple> = new Set();

    // dont check completed entities twice
    if (this.compEntityByNode(startNode) != null) {
      return false;
    }

    // build w-wr graph
    const graphArr: KNodeSet = this.buildGraph(startNode);

    // check if complete
    const complete = this.isComplete(graphArr);
    if (!complete) return false;

    logger.info('Wald complete.', startNode.type, complete);

    if (complete && this.entityMap.get(startNode.type).completable) {
      this.compEntityArr.push(graphArr);
    }

    // loop completed graph - find meeples
    for (const node of graphArr) {
      if (node.meeple && !node.meeple.recovered) {
        updatedMeeples.add(node.meeple);
      }
    }

    // recover meeples, place resources
    this.recoverMeeples(updatedMeeples);
  }

  checkCompletableRoad(startNode: KNode) {
    const updatedMeeples: Set<Meeple> = new Set();

    // dont check completed entities twice - disabled to allow taxman recovery on already completed road graphs
    if (this.compEntityByNode(startNode) != null) {
      // return false;
    }

    // build r graph
    const graphArr: KNodeSet = this.buildGraph(startNode);

    // check if complete
    const complete = this.isComplete(graphArr);
    if (!complete) return false;

    logger.info('Road complete.', startNode.type);

    // check if connected w graphs are completed and if yes resources are harvested
    let uncompletedWald = Array.from(graphArr.values()).some((node) => {
      if (node.wald?.length > 0) {
        const wrNode = this.nodeByIdTileId(node.tile.id, node.wald[0]);
        // no meeple to wait for
        if (!wrNode.meeple) return false;
        // check if meeple player id is the same as taxman player id -> no tax to collect here
        if (wrNode.meeple.playerId === startNode.meeple?.playerId) return false;
        const wGraph = this.compEntityByNode(wrNode);
        // wald not compeletd
        if (!wGraph) return true;
        // wald completed, but unharvested
        const hasUnharvestedResource = wrNode.resource && !wrNode.resource.harvested;
        if (hasUnharvestedResource) {
          logger.info('Wald completed, but unharvested resource found.');
          return true;
        }
        return false;
      }
    });

    if (uncompletedWald) {
      logger.info('Uncompleted wald found, not completeing road.');
      return false;
    }

    if (this.entityMap.get(startNode.type).completable) {
      this.compEntityArr.push(graphArr);
    }

    // loop completed graph - find meeples
    for (const node of graphArr) {
      if (node.meeple && !node.meeple.recovered) {
        updatedMeeples.add(node.meeple);
      }
    }

    // recover meeples, place resources
    if (complete) {
      this.recoverMeeples(updatedMeeples);
    }
  }

  protected updatePlayerScores(
    meepleCount: Map<number, number>,
    points: number,
    pigCount: Map<number, number>
  ): KPlayer[] {
    const maxCount = Math.max(...meepleCount.values());
    const maxPlayers: KPlayer[] = [];
    for (const playerId of meepleCount.keys()) {
      const player = this.playerList.playerById(playerId);
      const count = meepleCount.get(playerId);
      if (count == maxCount) {
        maxPlayers.push(player);
        if (pigCount.has(playerId)) {
          player.addScore((points * 4) / 3);
        } else {
          player.addScore(points);
        }
      }
    }
    return maxPlayers;
  }

  protected recoverMeeples(updatedMeeples: Set<Meeple>) {
    for (const meeple of updatedMeeples) {
      const player: KPlayer = this.playerList.playerById(meeple.playerId);
      // add KResources for completed wr
      if (meeple.nodeType === 'wr') {
        const resource = new KResource();
        resource.id = meeple.id;
        resource.playerId = meeple.playerId;
        resource.color = meeple.color;
        resource.nodeId = meeple.nodeId;
        resource.tileId = meeple.tileId;
        this.tileById(meeple.tileId).resource = resource;
        this.nodeByIdTileId(meeple.tileId, meeple.nodeId).resource = resource;
      }
      // recover meeple
      player.recoverMeeple(meeple as KMeeple);
      this.recoveredMeeples.push(meeple);
    }
  }

  buildGraph(rootNode: KNode): KNodeSet {
    const nodeArr = [rootNode];
    const graphArr: KNodeSet = new Set();
    while (nodeArr.length > 0) {
      const node = nodeArr.shift();
      if (![...graphArr.values()].includes(node)) graphArr.add(node);
      for (const newNode of node.nbs) {
        const f1 = !nodeArr.includes(newNode);
        const f2 = ![...graphArr.values()].includes(newNode);
        if (f1 && f2) nodeArr.push(newNode);
      }
    }

    return graphArr;
  }

  /**
   * Graph also spans over r-wr-r / r-c-r connections
   */
  buildExtendedGraph(rootNode: KNode): KNodeSet {
    const nodeArr = [rootNode];
    const graphArr: KNodeSet = new Set();
    while (nodeArr.length > 0) {
      const node = nodeArr.shift();
      if (![...graphArr.values()].includes(node)) graphArr.add(node);
      const nbs = [...node.nbs];
      // add wr node
      if (node.type === 'r' && node.wald?.length > 0) nbs.push(this.nodeByIdTileId(node.tile.id, node.wald[0]));
      // add r nodes of wr node
      if (node.type === 'wr' && node.roads?.length > 0)
        node.roads.forEach((n, i) => nbs.push(this.nodeByIdTileId(node.tile.id, node.roads[i])));
      // add city node
      if (node.type === 'r' && node.cities?.length > 0) nbs.push(this.nodeByIdTileId(node.tile.id, node.cities[0]));
      // add r nodes of city node
      if (node.type === 'c' && node.roads?.length > 0)
        node.roads.forEach((n, i) => nbs.push(this.nodeByIdTileId(node.tile.id, node.roads[i])));

      for (const newNode of nbs) {
        const f1 = !nodeArr.includes(newNode);
        const f2 = ![...graphArr.values()].includes(newNode);
        if (f1 && f2) nodeArr.push(newNode);
      }
    }

    return graphArr;
  }
  protected isComplete(graphArr: KNodeSet): boolean {
    for (const node of graphArr) {
      if (node.nbs == null) {
        return false;
      } else if (node.conns.length != node.nbs.length) return false;
    }
    return true;
  }

  protected compEntityByNode(myNode: KNode): Set<KNode> {
    for (const entity of this.compEntityArr) {
      if ([...entity.values()].includes(myNode)) return entity;
    }
    return null;
  }

  updateTile(tileDTO: KTileDTO, tile: KTile) {
    tile.rot = tileDTO.rot;
    tile.place = new Place(
      tileDTO.shape,
      tileDTO.place?.positions.map((pos) => new KPosition().init(pos.x, pos.y, pos.slot))
    );
    tile.isTemp = tileDTO.isTemp;

    if (tileDTO.meeple != null) {
      const node: KNode = this.nodeById(tile, tileDTO.meeple.nodeId);
      // get player meeple by type
      // for temp tile use temp meeple in order not use up player meeples
      const meeple: Meeple = tileDTO.isTemp
        ? Meeple.fromDTO(tileDTO.meeple)
        : this.playerList.actPlayer.activateMeepleByType(tileDTO.meeple.type);
      if (meeple == null) {
        logger.error('Meeple not found on player.', tileDTO.meeple);
        throw new Error('Meeple not found on player.');
      }
      this.placeMeeple(meeple, node, tile);
    } else {
      tile.meeple = null;
    }

    //this.tileData.updateOpenPlaces();
  }

  nodeById(tile: KTile, nodeId: number): KNode {
    return tile.nodes.find((n) => n.id == nodeId);
  }

  tileById(tileId: number): KTile {
    return Array.from(this.tileData.values()).find((t) => t.id === tileId);
  }

  nodeByIdTileId(tileId: number, nodeId: number): KNode {
    const tile = this.tileById(tileId);
    return tile ? this.nodeById(tile, nodeId) : null;
  }

  protected placeMeeple(meeple: Meeple, node: KNode, tile: KTile) {
    meeple.nodeId = node.id;
    meeple.nodeType = node.type;
    meeple.tileId = tile.id;
    node.meeple = meeple;
    tile.meeple = meeple;
  }

  protected addTaxMeeple(meeple: Meeple, node: KNode, tile: KTile) {
    meeple.nodeId = node.id;
    meeple.nodeType = node.type;
    meeple.tileId = tile.id;
    node.meeple = meeple;
    tile.taxMeeples.push(meeple);
  }

  setNodeConnections(actTile: KTile, tileData: KTileData) {
    for (const node of actTile.nodes) {
      for (const conn of node.conns) {
        const nextNode = this.getConnectedNode(node, conn, actTile);
        if (nextNode == node) {
          //log.error("getConnectedNode error!!");
        }
        if (nextNode == null) continue;
        //if (nextNode.type !== node.type) continue;
        if (!node.nbs.includes(nextNode)) node.nbs.push(nextNode);
        //if (!nextNode.getNbs().contains(node)){
        if (!nextNode.nbs.includes(node)) nextNode.nbs.push(node);
        //}
      }
    }
  }

  getConnectedNode(node: KNode, conn: number, actTile: KTile): KNode {
    // ? why is this transform not needed ???
    // const sideIndex = this.connToSide(node, conn);
    let externalIndex: number = -1;
    let externalTile: KTile = null;
    // find external tile and external sideIndex for given sideIndex
    const nbs: KTileNbs = this.tileData.getTileNbs(actTile.place);
    for (const [pos, { originalPos, nbTile }] of Array.from(nbs.nbs.entries())) {
      const [x, y, slot] = pos.split(':').map(Number);
      const [x0, y0, slot0] = originalPos.split(':').map(Number);
      const side = this.rotSide(actTile, slot0, slot);
      const externalSide = this.rotSide(nbTile, slot, slot0);
      // find nb whose externalSide eqeauls side and sideIndex matches original sideIndex
      const originalSideIndex = this.rotSideIndex(actTile, slot0, slot);
      if (originalSideIndex === conn && side === externalSide) {
        externalIndex = this.rotSideIndex(nbTile, slot, slot0);
        externalTile = nbTile;
        break;
      }
    }
    if (externalIndex === -1) {
      return null;
    }
    const connIndex = externalIndex;
    // find teh node that connects to that external sideIndex
    let connectedNode: KNode = null;
    for (const nodeItem of externalTile.nodes) {
      //if (nodeItem.type !== node.type) continue;

      for (const conn of nodeItem.conns) {
        if (conn == connIndex) {
          connectedNode = nodeItem;
        }
      }
    }
    return connectedNode;
  }

  calculateCurrentActions(lastTileDTO: KTileDTO): KActionDTO[] {
    if (!lastTileDTO) return;
    const lastTile = this.lastTiles.find((t) => t.id === lastTileDTO.id);
    // if temp file, remove it temporarily
    if (lastTileDTO.isTemp) {
      this.tileData.removeTile(lastTile.toDTO());
    }
    this.tileData.updateOpenPlaces();
    const actPlayer = this.playerList.actPlayer;
    const availableMeepleTypes: MeepleType[] = actPlayer.availableTypes();
    this.currentActions = [];
    const openPlacesByShape = Array.from(this.tileData.openPlaces.values()).filter(
      (p) => toPlace(p).shape === lastTile.shape
    );
    for (const p of openPlacesByShape) {
      const place = toPlace(p);
      //find nb tiles
      const nbTiles: KTileNbs = this.tileData.getTileNbs(place);
      // find rotatedMatchingPositions
      for (const rot of this.getRotationAngles(lastTile)) {
        const tile = this.checkTilePos(toPlace(p), lastTile, nbTiles, rot);
        if (tile == null) continue;
        // add empty tile
        this.currentActions.push({ tile: tile.toDTO() });
        // for resource node - add meeple action
        if (availableMeepleTypes.length > 0) {
          for (const node of tile.nodes) {
            // wald resource
            if (node.type === 'wr') {
              for (const availableType of availableMeepleTypes) {
                const tileDTO = this.createAction(
                  tile,
                  rot,
                  toPlace(p),
                  node,
                  actPlayer,
                  actPlayer.getMeepleByType(availableType)
                );
                this.currentActions.push({ tile: tileDTO });
              }
            }
          }
        }
      }
    }
    // if temp file, readd it back
    if (lastTileDTO.isTemp) {
      this.tileData.set(lastTileDTO.place, lastTile);
    }

    return this.currentActions;
  }

  public removeNodeConnections(actTile: KTile) {
    for (const node of actTile.nodes) {
      node.nbs.forEach((nextNode) => nextNode.nbs.splice(nextNode.nbs.indexOf(node), 1));
      node.nbs = [];
    }
  }

  protected createAction(tile: KTile, rot: number, place: Place, node: KNode, player: KPlayer, playerMeeple: Meeple) {
    const tileDTO = tile.toDTO();
    tileDTO.rot = rot;
    tileDTO.place = place;
    const meeple = playerMeeple.toDTO();
    meeple.playerId = player.id;
    meeple.color = player.color;
    meeple.nodeId = node.id;
    tileDTO.meeple = meeple;
    return tileDTO;
  }

  removePlayerMeeples(playerId: number) {
    for (const tile of this.tileData.values()) {
      if (tile.meeple && tile.meeple.playerId == playerId) {
        tile.meeple = null;
      }
      for (const node of tile.nodes) {
        if (node.meeple && node.meeple.playerId == playerId) {
          node.meeple = null;
        }
      }
    }
  }

  // checks if tile's each rotated side is matching with neighbor tile's side
  protected checkTilePos(originalPlace: Place, tile: KTile, nbs: KTileNbs, rot: number): KTile {
    // create new tile so that lastTile is not mutated

    const t: KTile = this.createTile(tile.type, tile.id);
    t.rot = rot;
    t.place = originalPlace;

    // checks if tile's each rotated side is matching with neighbor tile's side
    for (const [pos, { originalPos, nbTile }] of Array.from(nbs.nbs.entries())) {
      const [x, y, slot] = pos.split(':').map(Number);
      const [x0, y0, slot0] = originalPos.split(':').map(Number);
      if (this.rotSide(nbTile, slot, slot0) !== this.rotSide(t, slot0, slot)) return null;
    }

    return t;
  }

  protected isMeepleCompatibleWithExistingMeeples(
    meepleType: MeepleType,
    existingMeeples: Map<string, Meeple>
  ): boolean {
    // meeple, giant, ship should not conflict
    if ((meepleType === 'meeple' || meepleType === 'giant' || meepleType === 'ship') && existingMeeples.has('conflict'))
      return false;
    return true;
  }

  protected getRotationAngles(tile: KTile): number[] {
    if (tile.shape === 'hexa') {
      return [0, 60, 120, 180, 240, 300];
    } else if (tile.shape === 'rhombus') {
      return [0, 180];
    } else if (tile.shape === 'triangle') {
      return [0, 120, 240];
    }
    return [];
  }

  protected isMeepleCompatibleWithNode(meepleType: MeepleType, node: KNode): boolean {
    return true;
  }

  protected rotSideIndex(tile: KTile, slot: number, externalSlot?: number): number {
    if (tile.shape === 'hexa') {
      const sideIndex = hexaSlotToSideIndex(tile.place, slot);
      return this.rotSideIndexHexa(tile, sideIndex, -1);
    }
    if (tile.shape === 'triangle') {
      const sideIndex = triangleSlotToSideIndex(tile.place, slot, externalSlot);
      return this.rotSideIndexTriangle(tile, sideIndex, -1);
    }
    if (tile.shape === 'rhombus') {
      const sideIndex = rhombusSlotToSideIndex(tile.place, slot, externalSlot);
      return this.rotSideIndexRhombus(tile, sideIndex, -1);
    }

    throw new Error('Invalid tile shape: ' + tile.shape);
  }

  protected rotSide(tile: KTile, slot: number, externalSlot?: number): string {
    return tile.sides.charAt(this.rotSideIndex(tile, slot, externalSlot));
  }

  // returns the rotated sideIndex of a given index for a hexa tile, result only depends on rotation
  protected rotSideIndexHexa(tile: KTile, sideIndex: number, dir: number): number {
    let rotSideIndex = sideIndex + (dir * tile.rot) / 60;
    while (rotSideIndex < 0) rotSideIndex += 6;
    return +(rotSideIndex % 6);
  }

  // returns the rotated sideIndex of a given index for a triangle tile, result depends on rotation
  protected rotSideIndexTriangle(tile: KTile, sideIndex: number, dir: number): number {
    let rotSideIndex = sideIndex + (dir * tile.rot) / 120;
    while (rotSideIndex < 0) rotSideIndex += 3;
    return +(rotSideIndex % 3);
  }

  protected rotSideIndexRhombus(tile: KTile, sideIndex: number, dir: number): number {
    let rotSideIndex = sideIndex + (dir * tile.rot) / 90;
    while (rotSideIndex < 0) rotSideIndex += 4;
    return +(rotSideIndex % 4);
  }

  protected connToSide(node: KNode, conn: number): number {
    if (node.tile.shape === 'hexa') {
      const sideIndex = conn;
      const rotIndex = this.rotSideIndexHexa(node.tile, sideIndex, 1);
      return rotIndex;
    }
    if (node.tile.shape === 'rhombus') {
      const sideIndex = conn;
      const rotIndex = this.rotSideIndexRhombus(node.tile, sideIndex, 1);
      return rotIndex;
    }
    if (node.tile.shape === 'triangle') {
      const sideIndex = conn;
      const rotIndex = this.rotSideIndexTriangle(node.tile, sideIndex, 1);
      return rotIndex;
    }
  }

  evaluateTileUpdate() {
    logger.info('evaluateTileUpdate');
    // check completed wr/w graphs - forests to recover meeples
    for (const tile of this.lastTiles) {
      for (const node of tile.nodes) {
        if (node.type === 'w' || node.type === 'wr') this.checkCompletableWald(node);
      }
    }

    // check completed road graphs - to recover taxman meeples - the connected wr-graphs also need to be completed !!
    for (const tile of this.lastTiles) {
      for (const node of tile.nodes) {
        if (node.type === 'r') this.checkCompletableRoad(node);
        //  also check roads connected to completed w-wr graphs
        if (node.type === 'wr' || node.type === 'w') {
          const wGraph = this.compEntityByNode(node);
          if (wGraph) {
            const wrNodes = Array.from(wGraph.values()).filter((n) => n.type === 'wr');
            wrNodes.forEach((wrNode) => {
              // roads connected to the wr nodes
              wrNode.roads.forEach((rId) => {
                const rNode = this.nodeByIdTileId(wrNode.tile.id, rId);
                if (rNode.type === 'r') this.checkCompletableRoad(rNode);
              });
            });
          }
        }
      }
    }

    this.lastTiles = [];
    // check possible resource actions and if yes pass to resurce turn
    this.checkHarvestableResources();
  }

  @Property()
  harvestableResources: KResourceMap = new KResourceMap();

  @Property()
  taxableNodeMap: KTaxableNodeMap = new KTaxableNodeMap();

  findConnectedPortNodes(resource: KResource): KNode[] {
    if (resource.playerId !== this.playerList.actPlayer.id || resource.harvested) [];
    if (resource.harvested) [];
    // find connected 'r' nodes
    const wResourceNode = this.nodeByIdTileId(resource.tileId, resource.nodeId);
    const connectedRNodes = wResourceNode.roads.map((rId) => this.nodeByIdTileId(resource.tileId, rId));
    // for each connected rNode - build an 'r' graph
    // and collect connected city nodes
    const portNodes = new Array<KNode>();
    connectedRNodes.forEach((node) => {
      const rGraph = this.buildGraph(node);
      Array.from(rGraph)
        .filter((n) => n.cities?.length > 0)
        .forEach((portNode) => {
          if (!portNodes.includes(portNode)) {
            portNodes.push(portNode);
          }
        });
    });
    return portNodes;
  }

  // collect unharvested resources with city-connection for user into a map which will hold a move / resource
  checkHarvestableResources() {
    // filter unharvested resources for user
    // highlight harvestable resources
    const resources: KResourceMap = new KResourceMap();
    this.tiles().forEach((t) => {
      if (t.resource && t.resource?.playerId === this.playerList.actPlayer.id && !t.resource?.harvested) {
        const portNodes = this.findConnectedPortNodes(t.resource);
        if (portNodes.length > 0) {
          this.turn = 'resource';
          const kr = new ResourceMove();
          kr.resource = t.resource;
          kr.portNodes = portNodes;
          resources.map.set(t.resource.id, kr);
        }
      }
    });
    logger.info('harvestableResources', resources);
    this.harvestableResources = resources;
  }

  finalUpdate() {
    logger.info('finalUpdate');
    this.lastTiles = [];
    // evaluate player scores
    for (const tile of this.tileData.values()) {
      if (tile.shape === 'hexa') {
        if (tile.playerResource) {
          // add city scores
          let maxScore = Math.max(...Array.from(tile.playerResource.map.values()).map((r) => r.score));
          const maxPlayerIds = Array.from(new Set(Array.from(tile.playerResource.map.values())))
            .filter((r) => r.score === maxScore)
            .map((r) => r.playerId);
          // build all r-graphs - roads should go thru forests and cities!
          // check if king's castle is connected to any of them
          /*
          const cityNode = tile.nodes.find((n) => n.type === 'c');
          const extendedGRaph = this.buildExtendedGraph(cityNode);
          const connectedToKingsCastle = Array.from(extendedGRaph).some((n) => {
            if (n.type === 'c' && n.tile.type === 49) return true;
          });
          if (connectedToKingsCastle && tile.type !== 49) maxScore = maxScore + 2;
          */
          const nrOfGates = tile.nodes.filter((n) => n.type === 'r').length;
          maxPlayerIds.forEach((playerId) => {
            this.playerList.playerById(playerId).addScore(nrOfGates + tile.mineCount);
          });
        }
      }
    }
  }
}

export default KTileLogic;
